/**
 * AuditSearchContext — 세번분류 검색 교차패널 연동 상태 버스.
 *
 * LeftAiPanel(검색 writer)이 사이드바로 이전돼도 EvidencePanel(우측 근거)·
 * MultiSearchResults(가운데 종합뷰)·GoogleImageOverlay(가운데)가 프롭 드릴링 없이
 * 같은 상태를 공유하도록 루트(index.js)에서 provider 주입.
 *
 *  searchContext : { q, matchTokens, semanticOn, expand } — EvidencePanel 하이라이트·의미검색
 *  searchResults : tariff-search 응답 끌어올림 — 종합 뷰 ① 재사용
 *  subdivisionPreview : 현재 검색어의 국내세분 결정표 compact shadow
 *  focusedCase   : 좌측 사례 클릭 상세 — EvidencePanel detail
 *  googleImageQ  : 구글 이미지 오버레이 검색어 — 가운데 GoogleImageOverlay
 *  pendingSearch : { q, ts } — 가운데 CenterSearchBar 제출 → LeftAiPanel 이 감지해 doSearch 실행
 *  searchSubmission : 마지막 명시적 검색 제출 — 결과 영역 스크롤 초기화 신호
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useState,
  useMemo,
} from "react";

const AuditSearchContext = createContext(null);

export function AuditSearchProvider({ children }) {
  const [searchContext, setSearchContext] = useState(null);
  const [searchResults, setSearchResults] = useState(null);
  const [subdivisionPreview, setSubdivisionPreview] = useState(null);
  const [focusedCase, setFocusedCase] = useState(null);
  const [googleImageQ, setGoogleImageQ] = useState(null);
  const [pendingSearch, setPendingSearchState] = useState(null);
  // pendingSearch는 LeftAiPanel이 처리 후 null로 비우므로 검색 제출 UI 신호로는
  // 사용할 수 없다. 마지막 제출은 별도로 보존해 같은 검색어 재검색도 구분한다.
  const [searchSubmission, setSearchSubmission] = useState(null);
  // 관세율표 조회(tariff-search)가 어느 검색어로 진행 중인지. 제안 카드는 이 조회가 끝나
  // 상품 설명이 함께 도착한 뒤에만 묻는다 — 마운트·인증 세대 갱신·상품 설명 도착에 각각
  // 묻던 3회를 1회로 합친다(2026-09-14 22차 실측 499·499·200).
  const [lookupInFlight, setLookupInFlight] = useState(null);
  const setPendingSearch = useCallback((nextSearch) => {
    setPendingSearchState(nextSearch);
    if (nextSearch?.q) setSearchSubmission(nextSearch);
  }, []);
  const value = useMemo(
    () => ({
      searchContext,
      setSearchContext,
      searchResults,
      setSearchResults,
      subdivisionPreview,
      setSubdivisionPreview,
      focusedCase,
      setFocusedCase,
      googleImageQ,
      setGoogleImageQ,
      pendingSearch,
      setPendingSearch,
      searchSubmission,
      lookupInFlight,
      setLookupInFlight,
    }),
    [
      searchContext,
      searchResults,
      subdivisionPreview,
      focusedCase,
      googleImageQ,
      pendingSearch,
      searchSubmission,
      setPendingSearch,
      lookupInFlight,
    ],
  );
  return (
    <AuditSearchContext.Provider value={value}>
      {children}
    </AuditSearchContext.Provider>
  );
}

export function useAuditSearch() {
  const ctx = useContext(AuditSearchContext);
  if (!ctx) {
    throw new Error("useAuditSearch must be used within AuditSearchProvider");
  }
  return ctx;
}
